import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { useSeo } from "@/hooks/useSeo";
import { PageIntro } from "@/components/site/PageIntro";
import { PriceTag } from "@/components/site/ServiceCard";
import { Loading, Empty, ErrorState, Reveal } from "@/components/site/Primitives";

const Meta = ({ label, values }) =>
  values?.length ? (
    <div><p className="eb-label mb-2">{label}</p><p className="text-sm text-zinc-300">{values.join(" · ")}</p></div>
  ) : null;

const ServiceRow = ({ s, i }) => (
  <Reveal>
    <article id={s.slug} className="grid lg:grid-cols-12 gap-10 py-14 border-b border-[#27272A]" data-testid={`service-detail-${s.slug}`}>
      <div className="lg:col-span-5">
        <p className="eb-label">{String(i + 1).padStart(2, "0")} / {s.category}{s.featured && " · Featured"}</p>
        <h2 className="mt-4 text-3xl md:text-5xl font-bold tracking-tight">{s.name}</h2>
        <p className="mt-4 text-zinc-400 leading-relaxed">{s.description || s.summary}</p>
        <div className="mt-8"><PriceTag service={s} large /></div>
        {s.available ? (
          <Link to={`/enquire?service=${s.slug}`} className="eb-btn-primary mt-8" data-testid={`service-detail-enquire-${s.slug}`}>
            {s.custom_quote ? "Request a quote" : "Submit a brief"}
          </Link>
        ) : <p className="mt-8 text-amber-400 text-sm">Temporarily unavailable</p>}
      </div>
      <div className="lg:col-span-7 grid sm:grid-cols-2 gap-8">
        <div>
          <p className="eb-label mb-3">Included</p>
          {s.deliverables?.length ? s.deliverables.map((d) => <p key={d} className="flex gap-2 text-zinc-300 text-sm py-1.5"><Check size={16} className="text-[#0055FF] mt-0.5 shrink-0" />{d}</p>)
            : <p className="text-sm text-zinc-500">Defined in your written quote.</p>}
        </div>
        <div>
          <p className="eb-label mb-3">Not included</p>
          {s.exclusions?.length ? s.exclusions.map((d) => <p key={d} className="flex gap-2 text-zinc-500 text-sm py-1.5"><X size={16} className="mt-0.5 shrink-0" />{d}</p>)
            : <p className="text-sm text-zinc-500">Confirmed during scoping.</p>}
        </div>
        <Meta label="Platforms" values={s.platforms} />
        <Meta label="Aspect ratios" values={s.aspect_ratios} />
        <Meta label="Durations" values={s.durations} />
        <div><p className="eb-label mb-2">Revisions</p><p className="text-sm text-zinc-300">{s.revision_limit != null ? `${s.revision_limit} round${s.revision_limit === 1 ? "" : "s"}` : "As agreed in scope"}</p></div>
        <div className="sm:col-span-2"><p className="eb-label mb-2">Turnaround</p><p className="text-sm text-zinc-300">{s.turnaround_text}</p></div>
      </div>
    </article>
  </Reveal>
);

export default function Services() {
  const { services, pages, loading, error, reload } = useSite();
  useSeo("services", "/services");
  return (
    <div data-testid="services-page">
      <PageIntro eyebrow="Services" title="Edits, scoped to the work." body="Every service lists what's included, what isn't, and how pricing and turnaround work." />
      {pages.length > 0 && (
        <div className="eb-container pt-12">
          <p className="eb-label mb-4">Find your format</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#27272A] border border-[#27272A]">
            {pages.map((p) => (
              <Link key={p.slug} to={`/services/${p.slug}`} className="bg-[#050505] p-6 hover:bg-[#0a0f1f] transition-colors group" data-testid={`landing-link-${p.slug}`}>
                <p className="font-display text-xl font-semibold group-hover:text-[#00E5FF]">{p.nav_label || p.title}</p>
                <p className="mt-2 text-sm text-zinc-500 line-clamp-3">{p.summary}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
      <div className="eb-container">
        {loading && <div className="py-16"><Loading /></div>}
        {error && <div className="py-16"><ErrorState message={error} onRetry={reload} /></div>}
        {!loading && !error && services.length === 0 && <div className="py-16"><Empty title="Services are being updated." /></div>}
        {services.map((s, i) => <ServiceRow key={s.id} s={s} i={i} />)}
      </div>
    </div>
  );
}
